import type { ReviewOutputDataT } from '../../../common/review-mode-side-panel';
import type { AnnotationLabelT, BaseInputDataT, BaseOutputLabelledDataT, GenericAnnotationReviewDataT, LabellingValidationsT } from '../../../common/types/types';
import type { TimeDisplayMode } from '../../types';
import type { BrushAnnotationT, CuboidAnnotationT, EllipseAnnotationT, FrameClassificationT, PointAnnotationT, PolygonAnnotationT, PolylineAnnotationT, RectangleAnnotationT, SkeletonAnnotationT } from './annotations.type';
export type { TaskLevelQuestionAnswersT } from '../../../common/types/types';
import type { OutputRelationshipT } from '../../../modernization-common/relationships';
import type { ImageOnChangeMetadata } from '../../types';
export type SuperpixelConfig = {
  superpixelSegmentsNumbers?: number[];
};
export type ConvertToOutputAnnotation<T> = Omit<T, 'annotationLabel'> & {
  id: string;
  trackingId?: string;
  annotationLabel: Omit<AnnotationLabelT, 'style'>;
};
export type OutputAnnotationT = ConvertToOutputAnnotation<RectangleAnnotationT> | ConvertToOutputAnnotation<PolygonAnnotationT> | ConvertToOutputAnnotation<PointAnnotationT> | ConvertToOutputAnnotation<EllipseAnnotationT> | ConvertToOutputAnnotation<PolylineAnnotationT> | ConvertToOutputAnnotation<CuboidAnnotationT> | ConvertToOutputAnnotation<SkeletonAnnotationT> | ConvertToOutputAnnotation<BrushAnnotationT> | ConvertToOutputAnnotation<SkeletonAnnotationT> | ConvertToOutputAnnotation<FrameClassificationT>;
type BaseFrameT = {
  imageUrl: string;
  annotations: OutputAnnotationT[];
};
export type InputFrameT = {
  annotationMLData?: OutputAnnotationT[];
  annotationConsensusData?: OutputAnnotationT[];
} & BaseFrameT;
export type OutputFrameT = BaseFrameT;
export type ImageInputDataT = {
  imageUrl: string;
  annotations: OutputAnnotationT[];
  relationships?: OutputRelationshipT[];
  annotationMLData?: OutputAnnotationT[];
  annotationConsensusData?: OutputAnnotationT[];
  reviewData: GenericAnnotationReviewDataT[];
  feedbackData?: ReviewOutputDataT;
  superpixelSegmentsNumbers?: number[];
  hideNoEntityCheckbox?: boolean;
  timeDisplayMode?: TimeDisplayMode;
} & BaseInputDataT;
export type MediaItemT = {
  url: string;
  label: string;
  referenceUri: string;
};
export type VideoInputDataT = {
  videoUrl: string;
  mediaFileReference?: string;
  frameRate?: number;
  frames: InputFrameT[];
  reviewData: GenericAnnotationReviewDataT[];
  feedbackData?: ReviewOutputDataT;
  hideNoEntityCheckbox?: boolean;
  timeDisplayMode?: TimeDisplayMode;
  multiVideoList?: MediaItemT[];
  relationships?: OutputRelationshipT[];
} & BaseInputDataT;
type PDFInputDataT = {
  pdfUrl: string;
  pdfNumOfPages: number;
  frames: InputFrameT[];
  reviewData: GenericAnnotationReviewDataT[];
  feedbackData?: ReviewOutputDataT;
  consensusDataFrames?: InputFrameT[];
  hideNoEntityCheckbox?: boolean;
  timeDisplayMode?: TimeDisplayMode;
  relationships?: OutputRelationshipT[];
} & BaseInputDataT;
export type InputDataT = ImageInputDataT | VideoInputDataT | PDFInputDataT;
export type ImageOutputLabelledDataT = {
  labellingType: 'IMAGE';
  imageUrl: string;
  annotations: OutputAnnotationT[];
  reviewData: GenericAnnotationReviewDataT[];
  feedbackData?: ReviewOutputDataT;
  annotationMLData?: OutputAnnotationT[];
  relationships?: OutputRelationshipT[];
} & SuperpixelConfig & BaseOutputLabelledDataT;
export type VideoOutputLabelledDataT = {
  labellingType: 'VIDEO';
  videoUrl: string;
  mediaFileReference?: string;
  frames: OutputFrameT[];
  reviewData: GenericAnnotationReviewDataT[];
  feedbackData?: ReviewOutputDataT;
  annotationMLData?: OutputAnnotationT[];
  frameRate?: number;
  multiVideoList?: MediaItemT[];
  relationships?: OutputRelationshipT[];
} & BaseOutputLabelledDataT;
export type PDFOutputLabelledDataT = {
  labellingType: 'PDF';
  pdfUrl: string;
  pdfNumOfPages: number;
  frames: OutputFrameT[];
  reviewData: GenericAnnotationReviewDataT[];
  feedbackData?: ReviewOutputDataT;
  annotationMLData?: OutputAnnotationT[];
  consensusDataFrames?: InputFrameT[];
} & BaseOutputLabelledDataT;
export type OutputLabelledDataT = ImageOutputLabelledDataT | VideoOutputLabelledDataT | PDFOutputLabelledDataT;
export type OutputLabellingValidationsT = {
  containsPendingItem: boolean;
  containsRejectedAnnotation: boolean;
} & LabellingValidationsT;
export type onStoreChangeT = (outputLabelledData: OutputLabelledDataT, validations: OutputLabellingValidationsT, metadata?: ImageOnChangeMetadata) => void;
export { OutputRelationshipT };